/**
 * PublicCustomList — renders a list page through an admin-authored template.
 *
 * The detail page has had this since custom layouts shipped; the list page fell
 * through to the card grid even when a builder had configured `view_type`
 * 'custom' and chosen a layout page, because the server never sent the layout
 * down. It does now, and this is what consumes it.
 *
 * ONE TEMPLATE, ONCE PER RECORD. The template describes a single record, the
 * same way the detail template does, so a builder writes one layout and it
 * works on both surfaces. Each record is rendered through its own
 * PublicCustomLayout with its own scope — which is also what makes the related
 * data work: the server attaches every record's matched rows onto that record,
 * so `{{ product.name }}` inside a card resolves against THAT card's product,
 * not the first one on the page.
 *
 * Buttons are resolved per record with placement 'card', so a button's
 * `visible_when` is evaluated against the record it sits on. A registration
 * button on a closed event is hidden on that card alone.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeContext';
import PublicCustomLayout from './PublicCustomLayout';
import ScopedCustomStyle from './ScopedCustomStyle';
import PublicEmptyState from './PublicEmptyState';
import { COL_CLASS, DEFAULT_COL_CLASS } from './PublicCardGrid';
import { splitTemplate, buildPageScope } from '../../utils/splitTemplate';

export default function PublicCustomList({
  layout, records, related, actionButtons, actionForms,
  onAction, onSubmitForm, formState, columnsPerRow,
  detailEnabled = true, tableSlug,
  // Whether a search or filter is narrowing the list, and how to drop it.
  // Both default to the "no query" case so an existing caller that does not
  // pass them still gets correct wording rather than a filter hint it cannot
  // act on.
  hasActiveQuery = false, onClearQuery,
}) {
  const { theme } = useTheme();
  if (!layout || !layout.html) return null;

  const alias = layout.alias;
  // Same grid, same default (3 across) and same responsive behaviour as the
  // built-in card view — a custom template changes what a card CONTAINS, not
  // how many fit on a row.
  const colClass = COL_CLASS[String(columnsPerRow)] || DEFAULT_COL_CLASS;

  // The CSS the builder wrote alongside this template. The server has always
  // sent it; nothing rendered it, so a custom layout arrived unstyled and the
  // builder's card chrome silently did nothing. Emitted ONCE for the whole
  // list — scoping it per card would repeat the same rules N times.
  const listScopeId = 'arcee-custom-list';

  // Anything the builder marked as belonging to the page is lifted out and
  // drawn once; the rest is the card, repeated per record.
  const { pageHtml, recordHtml } = splitTemplate(layout.html);

  return (
    <div id={listScopeId}>
      <style>{cardElevationCss(listScopeId, theme.primary)}</style>
      <ScopedCustomStyle css={layout.css} scopeId={listScopeId} />

      {pageHtml && (
        <PublicCustomLayout
          html={pageHtml}
          // No record: a page zone is drawn once for the whole list, so a
          // placeholder naming the record resolves to nothing rather than
          // silently showing the first one's values.
          data={buildPageScope(related)}
          failed={failedFor(related)}
          placement="page"
          recordAlias={alias}
          scopeId="arcee-custom-page"
        />
      )}

      {/* A zero-record response used to fall straight through the map below
          and render nothing, so the page ended at the search bar. The page
          zone above still draws — only the card grid is replaced. */}
      {(records || []).length === 0 ? (
        <PublicEmptyState
          variant={hasActiveQuery ? 'no-results' : 'empty'}
          accentColor={theme.primary}
          title={hasActiveQuery ? 'No matching events' : 'No events on the calendar'}
          subtitle={
            hasActiveQuery
              ? 'Nothing matched your search or filters. Try a different term, or clear the filters to see everything.'
              : 'Nothing on the horizon just yet. Fresh events surface here the moment they are announced. Please stay tuned.'
          }
          onClear={hasActiveQuery ? onClearQuery : undefined}
        />
      ) : (
      <div className="row g-3">
      {(records || []).map((record, index) => {
        // The record carries its own related rows under the builder's chosen
        // names, attached server-side. Spreading it into the scope beside its
        // own alias means a template can address either `{{ event.title }}` or
        // a related `{{ organiser.name }}` without knowing which is which.
        const scope = { [alias]: record };
        for (const [key, value] of Object.entries(record || {})) {
          // Only the attached blocks — a scalar column belongs to the record,
          // not to the top level, or a column called `title` would shadow a
          // related block of the same name.
          if (value !== null && typeof value === 'object') scope[key] = value;
        }

        // A custom card opens its record exactly as a built-in one does, so
        // choosing a custom layout does not silently cost you navigation. It
        // follows the same "detail enabled" setting rather than inventing a
        // second switch, and is skipped when a record has no id to link to.
        const href = detailEnabled && tableSlug && record?.id != null
          ? `/p/${encodeURIComponent(tableSlug)}/${encodeURIComponent(record.id)}`
          : null;

        const card = (
          <PublicCustomLayout
              html={recordHtml}
              data={scope}
              failed={failedFor(related)}
              actionButtons={actionButtons}
              actionForms={actionForms}
              placement="card"
              recordAlias={alias}
              formState={formState}
              onAction={onAction ? button => onAction(button, record) : undefined}
              onSubmitForm={onSubmitForm}
              // Each card is its own style scope, so a template's CSS cannot
              // be applied twice to the same id.
              scopeId={`arcee-custom-card-${record?.id ?? index}`}
            />
        );

        return (
          <div className={`${colClass} arcee-list-card`} key={record?.id ?? index}>
            {href ? (
              <Link
                to={href}
                className="d-block h-100"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                {card}
              </Link>
            ) : card}
          </div>
        );
      })}
      </div>
      )}
    </div>
  );
}

/**
 * Elevation for every card, whatever template drew it.
 *
 * The card is the template's root element — the builder's CSS owns its shape,
 * border and radius, and the shadow follows that radius. The selector outranks
 * the builder's scoped `#list .card:hover` rules, so every list lifts the same
 * way; a soft wash of the theme accent joins the hover shadow.
 */
function cardElevationCss(scopeId, accent) {
  const card = `#${scopeId} .arcee-list-card .arcee-custom-layout > :first-child`;
  const hovered = `#${scopeId} .arcee-list-card:hover .arcee-custom-layout > :first-child`;
  return `
    ${card} {
      border-color: rgba(15, 23, 42, 0.06);
      box-shadow:
        0 1px 2px rgba(15, 23, 42, 0.04),
        0 4px 12px rgba(15, 23, 42, 0.06),
        0 16px 32px -12px rgba(15, 23, 42, 0.14);
      transition: box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease;
    }
    ${hovered} {
      border-color: ${accent}33;
      box-shadow:
        0 2px 4px rgba(15, 23, 42, 0.05),
        0 12px 24px rgba(15, 23, 42, 0.10),
        0 28px 48px -16px ${accent}40;
      transform: translateY(-4px);
    }
    @media (prefers-reduced-motion: reduce) {
      ${card}, ${hovered} { transition: none; transform: none; }
    }
  `;
}

/**
 * The aliases that failed, shared by every card.
 *
 * A source is resolved once for the whole page, so its failure is a property of
 * the page rather than of one record — every card shows the same "unavailable"
 * state, which is the truth.
 */
function failedFor(related) {
  const failed = new Set();
  for (const [alias, block] of Object.entries(related || {})) {
    if (block && block.status === 'error') failed.add(alias);
  }
  return failed;
}
